import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { services } from "@/data/services";

const navLinkClass = ({ isActive }) =>
  `px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
    isActive ? "text-white bg-white/10" : "text-slate-200 hover:text-white hover:bg-white/5"
  }`;

export const Wordmark = ({ dark = false }) => (
  <span className={`font-heading text-2xl font-extrabold tracking-tight ${dark ? "text-[#0B2545]" : "text-white"}`}>
    UMI<span className="text-[#17B8A6]">.</span>
  </span>
);

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const servicesActive = location.pathname.startsWith("/services");

  return (
    <header className="sticky top-0 z-50 bg-[#0B2545]/90 backdrop-blur-md text-white border-b border-white/10">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex items-center justify-between h-16">
        <Link to="/" data-testid="nav-brand-logo" aria-label="UMI IT Services home" onClick={() => setMobileOpen(false)}>
          <Wordmark />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
          <NavLink to="/" end data-testid="nav-link-home" className={navLinkClass}>Home</NavLink>
          <div className="relative group">
            <NavLink
              to="/services"
              data-testid="nav-link-services"
              className={`${navLinkClass({ isActive: servicesActive })} inline-flex items-center gap-1`}
            >
              Services <ChevronDown size={14} aria-hidden="true" />
            </NavLink>
            <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity duration-150 absolute left-0 top-full pt-2">
              <div className="w-72 rounded-xl bg-white shadow-xl border border-slate-100 py-2">
                {services.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`nav-dropdown-${s.slug}`}
                    className="block px-4 py-2.5 hover:bg-slate-50 transition-colors duration-150"
                  >
                    <span className="block text-sm font-semibold text-[#0B2545]">{s.title}</span>
                    <span className="block text-xs text-slate-500 mt-0.5">{s.tagline}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <NavLink to="/regulated-industries" data-testid="nav-link-regulated" className={navLinkClass}>Regulated Industries</NavLink>
          <NavLink to="/about" data-testid="nav-link-about" className={navLinkClass}>About</NavLink>
          <NavLink to="/blog" data-testid="nav-link-blog" className={navLinkClass}>Blog</NavLink>
          <NavLink to="/contact" data-testid="nav-link-contact" className={navLinkClass}>Contact</NavLink>
        </nav>

        <div className="flex items-center gap-4">
          <a href="tel:+61489222864" data-testid="header-phone-link"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-white transition-colors duration-150">
            <Phone size={15} className="text-[#17B8A6]" aria-hidden="true" /> 0489 222 864
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-book-consultation"
            className="hidden sm:inline-flex bg-[#17B8A6] hover:bg-[#0D9488] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors duration-200 hover:-translate-y-0.5"
          >
            Book a Free Consultation
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-3 -mr-2 text-white"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          data-testid="nav-mobile-drawer"
          className="lg:hidden bg-[#07182E] border-t border-white/10 px-4 py-4 space-y-1"
          aria-label="Mobile navigation"
        >
          <Link to="/" data-testid="mobile-nav-home" onClick={() => setMobileOpen(false)} className="block px-3 py-3 rounded-lg text-slate-100 hover:bg-white/5 font-medium">Home</Link>
          <Link to="/services" data-testid="mobile-nav-services" onClick={() => setMobileOpen(false)} className="block px-3 py-3 rounded-lg text-slate-100 hover:bg-white/5 font-medium">Services</Link>
          {services.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`mobile-nav-${s.slug}`}
              onClick={() => setMobileOpen(false)}
              className="block pl-7 pr-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5"
            >
              {s.title}
            </Link>
          ))}
          <Link to="/regulated-industries" data-testid="mobile-nav-regulated" onClick={() => setMobileOpen(false)} className="block px-3 py-3 rounded-lg text-slate-100 hover:bg-white/5 font-medium">Regulated Industries</Link>
          <Link to="/about" data-testid="mobile-nav-about" onClick={() => setMobileOpen(false)} className="block px-3 py-3 rounded-lg text-slate-100 hover:bg-white/5 font-medium">About</Link>
          <Link to="/blog" data-testid="mobile-nav-blog" onClick={() => setMobileOpen(false)} className="block px-3 py-3 rounded-lg text-slate-100 hover:bg-white/5 font-medium">Blog</Link>
          <Link to="/contact" data-testid="mobile-nav-contact" onClick={() => setMobileOpen(false)} className="block px-3 py-3 rounded-lg text-slate-100 hover:bg-white/5 font-medium">Contact</Link>
          <a href="tel:+61489222864" data-testid="mobile-phone-link"
            className="flex items-center justify-center gap-2 mt-3 border border-white/20 text-white font-semibold px-5 py-3.5 rounded-lg">
            <Phone size={16} className="text-[#17B8A6]" aria-hidden="true" /> Call 0489 222 864
          </a>
          <Link
            to="/contact"
            data-testid="mobile-cta-book-consultation"
            onClick={() => setMobileOpen(false)}
            className="block mt-2 text-center bg-[#17B8A6] hover:bg-[#0D9488] text-white font-semibold px-5 py-3.5 rounded-lg transition-colors duration-200"
          >
            Book a Free Consultation
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Header;
